import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import NavigationHeader from '../../components/ui/NavigationHeader';
import TrustSignals from '../../components/ui/TrustSignals';
import BreadcrumbNavigation from './components/BreadcrumbNavigation';
import ArchivePreview from './components/ArchivePreview';
import MetadataPanel from './components/MetadataPanel';
import TechnicalDetails from './components/TechnicalDetails';
import ActionPanel from './components/ActionPanel';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const ArchiveDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [archiveData, setArchiveData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Mock archive data - in real app this would come from API/blockchain
  const mockArchiveData = {
    id: id || "ARK-2024-001",
    title: "OpenAI GPT-4 Documentation",
    originalUrl: "https://platform.openai.com/docs/models/gpt-4",
    timestamp: "09/07/2024 08:42:57",
    ipfsHash: "QmYjtig7VJQ6XsnUjqqJvj7QaMcCAwtrgNdahSiFofrE7o",
    transactionHash: "0x742d35Cc6634C0532925a3b8D4C9db96590b5b8c1a2b3c4d5e6f7g8h9i0j1k2l",
    blockNumber: 45892341,
    contractAddress: "0x1234567890123456789012345678901234567890",
    gasUsed: 125000,
    gasPrice: 30,
    confirmations: 1247,
    nonce: 42,
    contentHash: "0xabcdef1234567890abcdef1234567890abcdef1234567890abcdef1234567890",
    merkleRoot: "0x9876543210987654321098765432109876543210987654321098765432109876",
    screenshot: {
      url: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=1200&h=800&fit=crop",
      resolution: "1920x1080",
      fileSize: "2.4 MB",
      format: "PNG",
      quality: "High"
    },
    gatewayUrls: [
      "https://gateway.pinata.cloud/ipfs/",
      "https://ipfs.io/ipfs/",
      "https://cloudflare-ipfs.com/ipfs/"
    ]
  };

  useEffect(() => {
    // Simulate loading archive data
    const loadArchiveData = async () => {
      try {
        setLoading(true);
        // Simulate API call delay
        await new Promise(resolve => setTimeout(resolve, 1500));
        setArchiveData(mockArchiveData);
      } catch (err) {
        setError('Failed to load archive details. Please try again.');
        console.error('Error loading archive:', err);
      } finally {
        setLoading(false);
      }
    };

    loadArchiveData();
  }, [id]);

  const handleRetry = () => {
    setError('');
    setLoading(true);
    // Retry loading
    setTimeout(() => {
      setArchiveData(mockArchiveData);
      setLoading(false);
    }, 1000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <NavigationHeader />
        <div className="pt-16">
          <div className="container mx-auto px-4 py-8">
            <div className="flex items-center justify-center min-h-[60vh]">
              <div className="flex flex-col items-center space-y-4">
                <Icon name="Loader2" size={48} className="text-primary animate-spin" />
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-foreground">Loading Archive Details</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Fetching verification data from blockchain...
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <NavigationHeader />
        <div className="pt-16">
          <div className="container mx-auto px-4 py-8">
            <div className="flex items-center justify-center min-h-[60vh]">
              <div className="text-center max-w-md">
                <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">Failed to Load Archive</h3>
                <p className="text-sm text-muted-foreground mb-6">{error}</p>
                <div className="flex items-center justify-center space-x-3">
                  <Button
                    variant="outline"
                    iconName="ArrowLeft"
                    iconPosition="left"
                    onClick={() => navigate(-1)}
                  >
                    Go Back
                  </Button>
                  <Button
                    variant="default"
                    iconName="RefreshCw"
                    iconPosition="left"
                    onClick={handleRetry}
                  >
                    Retry
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <NavigationHeader />
      
      <div className="pt-16">
        <div className="container mx-auto px-4 py-8">
          {/* Breadcrumb Navigation */}
          <BreadcrumbNavigation archiveData={archiveData} />

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            {/* Left Column - Preview and Metadata */}
            <div className="xl:col-span-2 space-y-6">
              {/* Archive Preview */}
              <ArchivePreview archiveData={archiveData} />
              
              {/* Metadata Panel */}
              <MetadataPanel archiveData={archiveData} />
              
              {/* Technical Details - Mobile/Tablet */}
              <div className="xl:hidden">
                <TechnicalDetails archiveData={archiveData} />
              </div>
            </div>

            {/* Right Column - Actions and Trust Signals */}
            <div className="space-y-6">
              {/* Action Panel */}
              <ActionPanel archiveData={archiveData} />
              
              {/* Trust Signals */}
              <TrustSignals />
              
              {/* Technical Details - Desktop */}
              <div className="hidden xl:block">
                <TechnicalDetails archiveData={archiveData} />
              </div>
            </div>
          </div>

          {/* Bottom Actions - Mobile */}
          <div className="md:hidden mt-8 pt-6 border-t border-border">
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                iconName="ArrowLeft"
                iconPosition="left"
                onClick={() => navigate(-1)}
                fullWidth
              >
                Go Back
              </Button>
              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                onClick={() => navigate('/webpage-notarization')}
                fullWidth
              >
                New Archive
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArchiveDetails;